import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { formatDollars } from '../../../../utils/formatting';
import { normalizeOriginalsCompletedOrders } from '../../../../utils/originalsCompletedOrders';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useEditDeviationAction } from '../../../../ActionBar/actionHooks/useEditDeviationAction';
import RequestOriginalButton from '../../../../originals/RequestOriginalButton';

import s from './OriginalInfo.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const OriginalInfo: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const custom_exclusives = useFlag('custom_exclusives');

  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const handleEditClick = useEditDeviationAction(deviation);

  if (!custom_exclusives || !deviation.extended?.originals) {
    return null;
  }

  const {
    basePrice,
    showInProgressCount,
    inProgressOrdersCount = 0,
  } = deviation.extended.originals;
  const { total: completedOrdersCount } = normalizeOriginalsCompletedOrders(
    deviation.extended.originals
  );

  const formattedPrice = formatDollars(basePrice);

  return (
    <div className={s['root']}>
      <div className={s['header']}>{t('sidebar.originalInfo.header')}</div>
      <div className={s['title']}>{deviation.title}</div>

      <div className={s['info-row']}>
        <div className={s['info-label']}>
          {t('sidebar.originalInfo.startingPrice')}
        </div>
        <div className={s['info-value']}>{formattedPrice}</div>
      </div>

      {completedOrdersCount > 0 ? (
        <div className={s['info-row']}>
          <div className={s['info-label']}>
            {t('sidebar.originalInfo.completedOrders')}
          </div>
          <div className={s['info-value']}>
            {t('sidebar.originalInfo.orders', {
              count: completedOrdersCount,
            })}
          </div>
        </div>
      ) : null}

      {showInProgressCount && inProgressOrdersCount > 0 ? (
        <div className={s['info-row']}>
          <div className={s['info-label']}>
            {t('sidebar.originalInfo.inProgressOrders')}
          </div>
          <div className={s['info-value']}>
            {t('sidebar.originalInfo.orders', {
              count: inProgressOrdersCount,
            })}
          </div>
        </div>
      ) : null}

      {isOwner ? (
        <BrandedButton
          variant="originals"
          size="large"
          className={s['primary-button']}
          onClick={handleEditClick}
        >
          {t('common.edit')}
        </BrandedButton>
      ) : (
        <RequestOriginalButton
          className={s['primary-button']}
          deviation={deviation}
          size="large"
          sectionname="side-rail"
        />
      )}

      {isOwner ? (
        <BrandedButton
          variant="originals-outline"
          size="large"
          className={s['secondary-button']}
          href={Url.studio({
            section: 'orders-originals',
            deviationId: deviation.deviationId,
          })}
        >
          {t('originals.actions.manageOrders')}
        </BrandedButton>
      ) : (
        <BrandedButton
          variant="originals-outline"
          size="large"
          className={s['secondary-button']}
          href={Url.userMessageLink(deviation.author?.username ?? '')}
        >
          {t('originals.actions.sendMessage')}
        </BrandedButton>
      )}
    </div>
  );
};

OriginalInfo.displayName = 'OriginalInfo';

export default OriginalInfo;
